<template>
	<div
		:style="`
			background: ${isDark ? '#303030' : '#f5f5f5'};
			position: absolute;
			top: 0;
			left: 0;
			bottom: 0;
			right: 0;
			overflow-y: auto;
		`"
	>
		<v-container fluid class="pa-6">
			<!-- Page Header -->
			<v-row class="mb-4">
				<v-col>
					<h1 class="text-h5 font-weight-medium">
						{{ $t('text.reports') || 'Raporlar' }}
					</h1>
					<p class="text-body-2 text--secondary mt-1">
						Dışa aktarmak istediğiniz rapor türünü seçin
					</p>
				</v-col>
			</v-row>

			<!-- Report Cards Grid -->
			<v-row>
				<v-col
					v-for="(reportTemplate, index) in reportTemplates"
					:key="`reportTemplates_${index}`"
					cols="6"
					sm="4"
					md="3"
					lg="2"
				>
					<v-card
						elevation="0"
						class="report-card fill-height d-flex flex-column text-center"
						:class="{ 'report-card--dark': isDark }"
						outlined
						:to="`/${currentLang}/reports/export/${reportTemplate.uuid}`"
					>
						<!-- Icon -->
						<div class="pt-4 pb-2">
							<v-avatar
								:color="getReportColor(reportTemplate.predefined_type)"
								size="44"
							>
								<v-icon dark size="22">
									{{ getReportIcon(reportTemplate.predefined_type) }}
								</v-icon>
							</v-avatar>
						</div>

						<!-- Title & Description -->
						<!-- Title & Description -->
						<v-card-text class="py-2 px-3 flex-grow-1">
							<h3 class="text-body-2 font-weight-medium report-card__title">
								{{ $t('text.report-types.' + reportTemplate.predefined_type) }}
							</h3>
							<v-tooltip
								bottom
								max-width="300"
								:disabled="!isDescriptionTruncated(reportTemplate)"
							>
								<template v-slot:activator="{ on, attrs }">
									<p
										class="text-caption text--secondary mt-1 mb-0 report-card__description"
										v-bind="attrs"
										v-on="on"
									>
										{{ reportTemplate.explanation }}
									</p>
								</template>
								<span>{{ reportTemplate.explanation }}</span>
							</v-tooltip>
						</v-card-text>

						<!-- Filter Icons -->
						<div class="pb-3 px-2">
							<div
								v-if="getReportFilters(reportTemplate).length"
								class="d-flex justify-center ga-1"
							>
								<v-tooltip
									bottom
									v-for="filter in getReportFilters(reportTemplate)"
									:key="filter.key"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-icon
											small
											:color="isDark ? 'grey lighten-1' : 'grey'"
											v-bind="attrs"
											v-on="on"
										>
											{{ filter.icon }}
										</v-icon>
									</template>
									<span>{{ filter.label }}</span>
								</v-tooltip>
							</div>
							<div v-else class="filter-placeholder"></div>
						</div>
					</v-card>
				</v-col>
			</v-row>

			<!-- Empty State -->
			<v-row v-if="reportTemplates.length === 0">
				<v-col cols="12" md="6" class="mx-auto">
					<v-card elevation="0" outlined class="text-center pa-8">
						<v-icon size="64" color="grey lighten-1"
							>mdi-file-document-outline</v-icon
						>
						<h3 class="text-h6 mt-4 mb-2">Rapor Bulunamadı</h3>
						<p class="text-body-2 text--secondary">
							{{ $t('err.no-feedback-templates-found') }}
						</p>
					</v-card>
				</v-col>
			</v-row>
		</v-container>
		<router-view></router-view>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api';

const FILTER_DEFINITIONS = {
	date: { key: 'date', label: 'Tarih', icon: 'mdi-calendar-range' },
	job: { key: 'job', label: 'Pozisyon', icon: 'mdi-briefcase-outline' },
	owner: { key: 'owner', label: 'İş Ortağı', icon: 'mdi-account-tie' },
	interviewer: {
		key: 'interviewer',
		label: 'Mülakatçı',
		icon: 'mdi-account-voice',
	},
};

const INTERVIEWER_PICKER_TYPES = new Set([
	'applications',
	'hired-candidates',
	'application-status-change',
	'scorecard',
	'interview-history',
	'applications-with-interview',
	'pipeline-snapshot',
	'interviewer-activity',
]);

const REPORT_ICONS = {
	applications: 'mdi-file-account',
	'hired-candidates': 'mdi-account-check',
	'offer-reject': 'mdi-close-circle-outline',
	negative: 'mdi-thumb-down-outline',
	'position-based-applications': 'mdi-sitemap',
	'turknet-custom-position-based': 'mdi-sitemap',
	'department-based-applications': 'mdi-domain',
	'job-status-duration': 'mdi-timer-sand',
	'business-partner': 'mdi-handshake',
	'hiring-manager': 'mdi-account-tie',
	'open-position': 'mdi-briefcase-outline',
	'application-status-change': 'mdi-swap-horizontal',
	scorecard: 'mdi-clipboard-check-outline',
	'interview-history': 'mdi-account-clock',
	'applications-with-interview': 'mdi-calendar-account',
	'current-pipeline-snapshot': 'mdi-filter-variant',
	'pipeline-snapshot': 'mdi-chart-line-variant',
	requisitions: 'mdi-file-document-edit-outline',
	'interviewer-activity': 'mdi-account-supervisor',
};

const REPORT_COLORS = {
	applications: '#4CAF50',
	'hired-candidates': '#8BC34A',
	'offer-reject': '#F44336',
	negative: '#E91E63',
	'position-based-applications': '#607D8B',
	'turknet-custom-position-based': '#78909C',
	'department-based-applications': '#5C6BC0',
	'job-status-duration': '#795548',
	'business-partner': '#E91E63',
	'hiring-manager': '#9C27B0',
	'open-position': '#3F51B5',
	'application-status-change': '#FF5722',
	scorecard: '#7C4DFF',
	'interview-history': '#00BCD4',
	'applications-with-interview': '#009688',
	'current-pipeline-snapshot': '#FF9800',
	'pipeline-snapshot': '#2196F3',
	requisitions: '#FF6F00',
	'interviewer-activity': '#26A69A',
};

export default {
	name: 'ExportReport',
	computed: {
		...mapState({
			isDark: state => state.currentUser.isDark,
			lang: state => state.currentUser.lang,
			currentUser: state => state.currentUser.currentUser,
			currentLang: state => state.currentUser.lang,
		}),
	},
	data() {
		return {
			reportTemplates: [],
		};
	},
	mounted() {
		api.reportTemplates.get({ uuid: undefined }).then(response => {
			const { items } = response.data;
			this.reportTemplates = items;
		});
	},
	methods: {
		isDescriptionTruncated(report) {
			return report.explanation.length > 90;
		},
		getReportFilters(report) {
			const filters = [];

			if (report.is_date_picker_visible) {
				filters.push(FILTER_DEFINITIONS.date);
			}
			if (report.is_job_picker_visible) {
				filters.push(FILTER_DEFINITIONS.job);
			}
			if (report.is_owner_picker_visible) {
				filters.push(FILTER_DEFINITIONS.owner);
			}
			if (INTERVIEWER_PICKER_TYPES.has(report.predefined_type)) {
				filters.push(FILTER_DEFINITIONS.interviewer);
			}

			return filters;
		},
		getReportIcon(type) {
			return REPORT_ICONS[type] || 'mdi-file-chart-outline';
		},
		getReportColor(type) {
			return REPORT_COLORS[type] || '#757575';
		},
	},
};
</script>

<style scoped>
.report-card {
	border-radius: 12px !important;
	transition: all 0.2s ease;
	background: #ffffff;
	cursor: pointer;
	text-decoration: none;
}

.report-card--dark {
	background: #424242;
}

.report-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
	border-color: var(--v-primary-base) !important;
}

.report-card--dark:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4) !important;
}

.report-card__title {
	line-height: 1.3;
}

.report-card__description {
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
	line-height: 1.4;
}

.filter-placeholder {
	height: 20px;
}

.ga-1 {
	gap: 6px;
}
</style>
